<template>
    <div class="recurring-trip-settings">
        <h3>{{ trans('pages.recurring.title') }}</h3>

        <!-- Frequency Controls -->
        <div class="field frequency-field">
            <div class="frequency-controls">
                <ui5-label>{{ trans('pages.recurring.repeat_every') }}</ui5-label>
                <text-input
                    type="number"
                    :value="config.frequencyInterval"
                    @input="updateFrequencyInterval($event)"
                    class="frequency-input"
                    min="1"
                    max="52"
                    step="1"
                />
                <ui5-label>{{ trans('pages.recurring.weeks') }}</ui5-label>
            </div>
        </div>

        <!-- Days of Week Selector -->
        <div class="field">
            <ui5-label>{{ trans('pages.recurring.repeat_on') }}</ui5-label>
            <div class="days-selector">
                <ui5-button
                    v-for="day in daysOfWeek"
                    :key="day.value"
                    :class="{ 'day-selected': isDaySelected(day.value) }"
                    @click="toggleDay(day.value, $event)"
                    design="Transparent"
                    class="day-button"
                >
                    {{ day.label }}
                </ui5-button>
            </div>
            <p
                v-if="config.daysOfWeek.length === 0"
                class="error-message"
            >
                {{ trans('pages.recurring.select_at_least_one_day') }}
            </p>
        </div>

        <!-- End Type Selection -->
        <div class="field">
            <ui5-label class="field-label">{{ trans('pages.recurring.ends') }}</ui5-label>

            <div class="end-type-option">
                <ui5-radio-button
                    name="endType"
                    value="never"
                    :checked="config.endType === 'never'"
                    @click="updateEndType('never')"
                />
                <span class="radio-label">{{ trans('pages.recurring.never') }}</span>
            </div>

            <div class="end-type-option">
                <ui5-radio-button
                    name="endType"
                    value="on_date"
                    :checked="config.endType === 'on_date'"
                    @click="updateEndType('on_date')"
                />
                <span class="radio-label">{{ trans('pages.recurring.on_date') }}</span>
                <ui5-date-picker
                    format-pattern="yyyy-MM-dd"
                    :value="config.endDate"
                    @change="updateEndDate($event.detail.value)"
                    :min-date="config.startDate"
                    :disabled="config.endType === 'never'"
                    class="end-date-picker"
                />
            </div>
        </div>

        <!-- Warning for "never" end type -->
        <ui5-message-strip
            v-if="config.endType === 'never'"
            design="Warning"
            class="warning-strip"
        >
            {{ trans('pages.recurring.warning_8_months') }}
        </ui5-message-strip>

        <!-- Preview section -->
        <div
            class="preview-section"
            v-if="showPreview"
        >
            <ui5-button
                @click="loadPreview"
                :disabled="!canPreview"
                class="preview-button"
            >
                {{ trans('pages.recurring.show_preview') }}
            </ui5-button>

            <div
                v-if="previewLoading"
                class="preview-loading"
            >
                <spinner />
            </div>

            <div
                v-else-if="previewDates.length > 0"
                class="preview-dates"
            >
                <ui5-label>
                    {{ trans('pages.recurring.preview_label', { count: previewTotalCount }) }}
                </ui5-label>
                <div
                    class="virtual-scroll-container"
                    @scroll="handleScroll"
                    ref="scrollContainer"
                >
                    <div
                        class="virtual-scroll-spacer"
                        :style="{ height: totalHeight + 'px' }"
                    >
                        <ul
                            class="virtual-scroll-content"
                            :style="{ transform: `translateY(${offsetY}px)` }"
                        >
                            <li
                                v-for="date in visibleDates"
                                :key="date"
                                class="preview-date-item"
                            >
                                {{ formatDate(date) }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import deliveryDatesApi from '../../api/deliveryDates';
import { showErrorMessage } from '../../dialogs';
import Spinner from '../loader/Spinner';
import TextInput from '../TextInput';
import localization from '../../mixins/localization';

export default {
    components: {
        Spinner,
        TextInput
    },
    mixins: [localization],
    props: {
        config: {
            type: Object,
            required: true,
            validator(config) {
                return (
                    Object.prototype.hasOwnProperty.call(config, 'frequencyInterval') &&
                    Object.prototype.hasOwnProperty.call(config, 'daysOfWeek') &&
                    Object.prototype.hasOwnProperty.call(config, 'endType') &&
                    Object.prototype.hasOwnProperty.call(config, 'endDate') &&
                    Object.prototype.hasOwnProperty.call(config, 'startDate')
                );
            }
        },
        showPreview: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            daysOfWeek: [
                { value: 1, label: this.trans('pages.recurring.day_mon') },
                { value: 2, label: this.trans('pages.recurring.day_tue') },
                { value: 3, label: this.trans('pages.recurring.day_wed') },
                { value: 4, label: this.trans('pages.recurring.day_thu') },
                { value: 5, label: this.trans('pages.recurring.day_fri') },
                { value: 6, label: this.trans('pages.recurring.day_sat') },
                { value: 7, label: this.trans('pages.recurring.day_sun') }
            ],
            previewDates: [],
            previewTotalCount: 0,
            previewLoading: false,
            // Virtual scrolling properties
            itemHeight: 41, // Height of each list item in pixels (padding + border)
            containerHeight: 400, // Height of scrollable container
            scrollTop: 0,
            bufferSize: 5 // Number of extra items to render above/below viewport
        };
    },
    computed: {
        canPreview() {
            const hasBasicConfig = (
                this.config.frequencyInterval >= 1 &&
                this.config.daysOfWeek.length > 0 &&
                this.config.startDate
            );

            // If endType is 'on_date', endDate must be provided
            const hasValidEndDate = (
                this.config.endType === 'never' ||
                (this.config.endType === 'on_date' && this.config.endDate)
            );

            return hasBasicConfig && hasValidEndDate;
        },
        // Virtual scrolling computed properties
        totalHeight() {
            return this.previewDates.length * this.itemHeight;
        },
        visibleStartIndex() {
            return Math.max(0, Math.floor(this.scrollTop / this.itemHeight) - this.bufferSize);
        },
        visibleEndIndex() {
            const itemsInView = Math.ceil(this.containerHeight / this.itemHeight);
            return Math.min(
                this.previewDates.length,
                this.visibleStartIndex + itemsInView + this.bufferSize * 2
            );
        },
        visibleDates() {
            return this.previewDates.slice(this.visibleStartIndex, this.visibleEndIndex);
        },
        offsetY() {
            return this.visibleStartIndex * this.itemHeight;
        }
    },
    methods: {
        isDaySelected(dayValue) {
            return this.config.daysOfWeek.includes(dayValue);
        },

        toggleDay(dayValue, event) {
            const days = [...this.config.daysOfWeek];
            const index = days.indexOf(dayValue);

            if (index > -1) {
                days.splice(index, 1);
            } else {
                days.push(dayValue);
            }

            days.sort((a, b) => a - b);
            this.$emit('update:daysOfWeek', days);

            // Remove focus from button to prevent sticky hover/active state
            if (event && event.target) {
                event.target.blur();
            }
        },

        updateFrequencyInterval(value) {
            const interval = parseInt(value);
            if (!isNaN(interval) && interval >= 1 && interval <= 52) {
                this.$emit('update:frequencyInterval', interval);
            }
        },

        updateEndType(value) {
            this.$emit('update:endType', value);
            if (value === 'never') {
                this.$emit('update:endDate', '');
            }
        },

        updateEndDate(value) {
            this.$emit('update:endDate', value);
        },

        async loadPreview() {
            if (!this.canPreview) {
                return;
            }

            this.previewLoading = true;
            this.previewDates = [];
            this.previewTotalCount = 0;

            try {
                const response = await deliveryDatesApi.previewRecurringTrip({
                    frequencyInterval: this.config.frequencyInterval,
                    daysOfWeek: this.config.daysOfWeek.join(','),
                    startDate: this.config.startDate,
                    endType: this.config.endType,
                    endDate: this.config.endDate || ''
                });

                this.previewDates = response.data.data.occurrences || [];
                this.previewTotalCount = response.data.data.total_count || 0;

            } catch (error) {
                showErrorMessage(this.trans('pages.recurring.preview_error'));
                console.error('Preview error:', error);
            } finally {
                this.previewLoading = false;
            }
        },

        formatDate(dateStr) {
            return moment(dateStr)
                .format('dddd DD/MM/YYYY');
        },

        handleScroll(event) {
            this.scrollTop = event.target.scrollTop;
        }
    }
};
</script>
